import {
  Box,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import theme from "../../../../Constants/theme";
import { inputStyles } from "../../../../Constants/inputStyles";
import { useDispatch, useSelector } from "react-redux";
import { Save } from "@mui/icons-material";
import { updateLLMConfig } from "../../../../features/organization/orgSlice";
import { iconButtonStyles } from "../../../../Constants/buttonStyles";

const FALLBACK_LLM_CONFIG = {
  contextual_retriever: "gpt-4o-mini",
  graphrag: "gpt-4o-mini",
  evaluation: "gpt-4o-mini",
  query_preprocessing: "gpt-4o-mini",
  llm_reranking: "gpt-4o-mini",
  sqr: "gpt-4o-mini",
  text2sql: "gpt-4o-mini",
  generate_prompt: "gpt-4o-mini",
  generate_metadata: "gpt-4o-mini",
  generate_golden_qna: "gpt-4o-mini",
};

const CATEGORY_SUBCATS = {
  "Data Preprocessing": [
    { key: "contextual_retriever", label: "Contextual Retriever" },
    { key: "graphrag", label: "GraphRAG" },
  ],
  Evaluation: [{ key: "evaluation", label: "Evaluation Model" }],
  "Inference Time": [
    { key: "query_preprocessing", label: "Query Preprocessing" },
    { key: "llm_reranking", label: "LLM Reranking" },
    { key: "sqr", label: "Metadata Filtering" },
    { key: "text2sql", label: "Text2SQL" },
  ],
  "App Creation Automation": [
    { key: "generate_prompt", label: "Generate Prompt" },
    { key: "generate_metadata", label: "Generate Metadata" },
    { key: "generate_golden_qna", label: "Generate Golden QnA" },
  ],
};

const ALLOWED_LLM_CHOICES = {
  contextual_retriever: [
    "gpt-4o-mini",
    "gpt-4o",
    "gpt-4.1-nano",
    "gpt-4.1-mini",
    "gpt-4.1",
  ],
  graphrag: [
    "gpt-4o-mini",
    "gpt-4o",
    "gpt-4.1-nano",
    "gpt-4.1-mini",
    "gpt-4.1",
  ],
  evaluation: [
    "gpt-4o-mini",
    "gpt-4o",
    "gpt-4.1-nano",
    "gpt-4.1-mini",
    "gpt-4.1",
  ],
  query_preprocessing: [
    "gpt-4o-mini",
    "gpt-4o",
    "gpt-4.1",
    "o1",
    "o3",
    "o3-mini",
    "o4-mini",
    "gpt-4.1-nano",
    "gpt-4.1-mini",
  ],
  llm_reranking: [
    "gpt-4o-mini",
    "gpt-4o",
    "gpt-4.1",
    "o1",
    "o3",
    "o3-mini",
    "o4-mini",
    "gpt-4.1-nano",
    "gpt-4.1-mini",
  ],
  sqr: [
    "gpt-4o-mini",
    "gpt-4o",
    "gpt-4.1",
    "o1",
    "o3",
    "o3-mini",
    "o4-mini",
    "gpt-4.1-nano",
    "gpt-4.1-mini",
  ],
  text2sql: [
    "gpt-4o-mini",
    "gpt-4o",
    "gpt-4.1",
    "o1",
    "o3",
    "o3-mini",
    "o4-mini",
    "gpt-4.1-nano",
    "gpt-4.1-mini",
  ],
  generate_prompt: [
    "gpt-4o-mini",
    "gpt-4o",
    "gpt-4.1",
    "o1",
    "o3",
    "o3-mini",
    "o4-mini",
    "gpt-4.1-nano",
    "gpt-4.1-mini",
  ],
  generate_metadata: [
    "gpt-4o-mini",
    "gpt-4o",
    "gpt-4.1",
    "o1",
    "o3",
    "o3-mini",
    "o4-mini",
    "gpt-4.1-nano",
    "gpt-4.1-mini",
  ],
  generate_golden_qna: [
    "gpt-4o-mini",
    "gpt-4o",
    "gpt-4.1",
    "o1",
    "o3",
    "o3-mini",
    "o4-mini",
    "gpt-4.1-nano",
    "gpt-4.1-mini",
  ],
};

const LLMSelectorEval = ({ showSnackbar }) => {
  const dispatch = useDispatch();
  const org = useSelector((st) => st.organization.organization);
  // pick saved or fallback defaults
  const defaultConfig = org?.llm_config || FALLBACK_LLM_CONFIG;
  // restrict if only gpt-4o-mini should be selectable
  const restrictToMini = org?.subscription === "starter";

  const [selections, setSelections] = useState(defaultConfig);

  // if organization config arrives later, overwrite
  useEffect(() => {
    if (org?.llm_config) {
      setSelections(org.llm_config);
    }
  }, [org]);

  const handleChange = (key) => async (e) => {
    const newVal = e.target.value;
    const newSelections = { ...selections, [key]: newVal };
    setSelections(newSelections);
    try {
      await dispatch(
        updateLLMConfig({
          models: newSelections,
          org_id: org?._id?.["$oid"],
        })
      ).unwrap();
      showSnackbar("LLM configuration saved successfully", "success");
    } catch (err) {
      showSnackbar(
        typeof err === "string" ? err : "Failed to save LLM configuration",
        "error"
      );
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: theme.main.backgroundBlue,
        width: "90%",
        p: 4,
        mt: 4,
        borderRadius: "10px",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Box>
        <Typography variant="h6" sx={{ color: theme.textColors.dark.primary }}>
          Select Models for Different Tasks
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: theme.textColors.dark.secondary }}
        >
          Change any dropdown and click its 💾 to save your full configuration.
        </Typography>
      </Box>

      {Object.entries(CATEGORY_SUBCATS).map(([categoryLabel, subs]) => (
        <Box key={categoryLabel} sx={{ width: "100%" }}>
          <Typography
            variant="subtitle1"
            sx={{ mb: 2, color: theme.textColors.dark.primary }}
          >
            {categoryLabel}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              width: "100%",
              gap: 2,
            }}
          >
            {subs.map(({ key, label }) => (
              <Box
                key={key}
                display="flex"
                alignItems="center"
                gap={2}
                mb={2}
                width="100%"
              >
                <FormControl fullWidth variant="outlined">
                  <InputLabel id={`${key}-label`} sx={inputStyles.inputLabel}>
                    {label}
                  </InputLabel>
                  <Select
                    labelId={`${key}-label`}
                    id={`${key}-select`}
                    value={selections[key] || ""}
                    label={label}
                    onChange={handleChange(key)}
                    sx={inputStyles.select}
                    MenuProps={inputStyles.selectMenuProps}
                  >
                    {ALLOWED_LLM_CHOICES[key].map((opt) => (
                      <MenuItem
                        key={opt}
                        value={opt}
                        disabled={restrictToMini && opt !== "gpt-4o-mini"}
                      >
                        {opt}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            ))}

            <Divider sx={{ borderColor: theme.border.divider, mt: 2 }} />
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default LLMSelectorEval;
